/**
 * tw-variant-selector.js
 * 
 * Shared variant selection utilities for Tailwind PDP components.
 * Handles form updates, URL changes, and partial section updates.
 * Uses same approach as product-variant-picker-visual.liquid for third-party app compatibility.
 */

window.TwVariantSelector = (function() {
  'use strict';
  const latestRefreshRequestBySection = {};

  /**
   * Resolves a product path/URL for Section Rendering API requests.
   * @param {string} productUrl - From data-product-url on selector containers
   * @param {string} sectionId - Main product section id
   * @returns {string}
   */
  function resolveProductUrl(productUrl, sectionId) {
    const trimmed = (productUrl || '').trim();
    if (trimmed) return trimmed;

    const root = document.getElementById('MainProduct-' + sectionId);
    if (root) {
      const fromRoot = (root.getAttribute('data-url') || '').trim();
      if (fromRoot) return fromRoot;
    }

    const productInfo = document.querySelector('product-info[data-url]');
    if (productInfo) {
      const fromInfo = (productInfo.getAttribute('data-url') || '').trim();
      if (fromInfo) return fromInfo;
    }

    return window.location.pathname;
  }

  /**
   * Builds an absolute Section Rendering API URL.
   */
  function buildSectionFetchUrl(productUrl, sectionId, variantId, viewParam) {
    const base = new URL(resolveProductUrl(productUrl, sectionId), window.location.origin);
    base.searchParams.set('variant', String(variantId));
    base.searchParams.set('section_id', String(sectionId));
    if (viewParam) {
      base.searchParams.set('view', viewParam);
    }
    return base.toString();
  }

  /**
   * Updates the product form's variant input
   * @param {string} sectionId - The section ID
   * @param {string} variantId - The new variant ID
   */
  function updateFormVariant(sectionId, variantId) {
    const productForms = document.querySelectorAll(
      `#product-form-${sectionId}, #product-form-installment-${sectionId}, form[action*="/cart/add"]`
    );
    
    productForms.forEach(form => {
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = variantId;
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  /**
   * Updates the browser URL with the variant parameter
   * @param {string} variantId - The new variant ID
   */
  function updateUrl(variantId) {
    if (!variantId) return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variantId));
      window.history.replaceState({}, '', url.toString());
    } catch (e) {
      /* ignore invalid location */
    }
  }

  function getProductData(productId) {
    if (!productId) return null;
    const script = document.getElementById('TwPdpProductJSON-' + productId);
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return null;
    }
  }

  function getVariantFromProductData(productId, variantId) {
    const data = getProductData(productId);
    if (!data || !data.variants || !variantId) return null;
    for (let i = 0; i < data.variants.length; i++) {
      if (String(data.variants[i].id) === String(variantId)) {
        return data.variants[i];
      }
    }
    return null;
  }

  function formatMoney(cents, referenceElement) {
    if (typeof window.twFormatPrice === 'function') {
      return window.twFormatPrice(cents, referenceElement);
    }
    const amount = (cents / 100).toFixed(2);
    return '$' + amount;
  }

  function isVariantSellable(variant) {
    if (!variant || variant.available === false) return false;
    if (variant.inventory_management === 'shopify') {
      if (variant.inventory_policy === 'continue') return true;
      return (variant.inventory_quantity || 0) > 0;
    }
    return true;
  }

  function isVariantPreorder(variant) {
    if (!variant || variant.available === false) return false;
    if (variant.inventory_management === 'shopify') {
      return variant.inventory_policy === 'continue' && (variant.inventory_quantity || 0) <= 0;
    }
    return false;
  }

  function getPurchaseMode(scope) {
    const onetimeSelected = scope.querySelector(
      '[data-purchase-type="onetime"].tw-selected, [data-purchase-type="onetime"][aria-pressed="true"]'
    );
    return onetimeSelected ? 'onetime' : 'subscribe';
  }

  /**
   * Syncs preorder notice visibility (subscribe + pre-order variant only).
   */
  function syncPreorderUI(sectionId, variantId) {
    const root = document.getElementById('MainProduct-' + sectionId);
    if (!root) return;

    const scope =
      document.getElementById('shopify-section-' + sectionId) || root;
    const productId = root.getAttribute('data-product-id');
    const variant =
      variantId != null && variantId !== ''
        ? getVariantFromProductData(productId, variantId)
        : null;

    let availability = 'in_stock';
    if (variant) {
      if (!isVariantSellable(variant)) {
        availability = 'sold_out';
      } else if (isVariantPreorder(variant)) {
        availability = 'preorder';
      }
    } else if (variantId == null || variantId === '') {
      availability = root.getAttribute('data-tw-pdp-availability') || 'in_stock';
    }

    root.setAttribute('data-tw-pdp-availability', availability);
    root.setAttribute('data-tw-pdp-purchase-mode', getPurchaseMode(scope));
  }

  function notifyVariantChange(sectionId, variantId) {
    if (!variantId) return;
    try {
      document.dispatchEvent(
        new CustomEvent('tw:pdp-variant-changed', {
          bubbles: true,
          detail: { sectionId: String(sectionId), variantId: String(variantId) }
        })
      );
    } catch (e) {
      /* ignore */
    }
  }

  function getSellingPlanId(variant) {
    if (!variant) return '';
    const allocations = variant.selling_plan_allocations || [];
    if (!allocations.length) return '';
    const allocation = allocations[0];
    if (allocation.selling_plan_id) return String(allocation.selling_plan_id);
    if (allocation.selling_plan && allocation.selling_plan.id) {
      return String(allocation.selling_plan.id);
    }
    return '';
  }

  function getSubscriptionPriceCents(variant) {
    if (!variant) return 0;
    const allocations = variant.selling_plan_allocations || [];
    if (allocations.length > 0) {
      const allocation = allocations[0];
      if (typeof allocation.per_delivery_price === 'number') {
        return allocation.per_delivery_price;
      }
      if (typeof allocation.price === 'number') {
        return allocation.price;
      }
    }
    return typeof variant.price === 'number' ? variant.price : 0;
  }

  /**
   * Keeps subscribe header + CTA price in sync after bundle/qty changes.
   */
  function syncSubscribePricing(sectionId, variantId) {
    if (!sectionId || !variantId) return;

    const root = document.getElementById('MainProduct-' + sectionId);
    if (!root) return;

    const variant = getVariantFromProductData(root.getAttribute('data-product-id'), variantId);
    if (!variant) return;

    const scope =
      document.getElementById('shopify-section-' + sectionId) || root;
    const subscriptionPrice = getSubscriptionPriceCents(variant);
    const subscribeOpt = scope.querySelector('[data-purchase-type="subscribe"]');
    const wrapper = scope.querySelector('.tw-purchase-type-wrapper');

    scope.querySelectorAll('.tw-subscription-price').forEach(function (el) {
      el.textContent = formatMoney(subscriptionPrice, el);
    });

    if (subscribeOpt) {
      subscribeOpt.setAttribute('data-price', String(subscriptionPrice));
    }
    if (wrapper) {
      wrapper.setAttribute('data-subscription-price', String(subscriptionPrice));
    }

    const compareEl = scope.querySelector('.tw-subscription-compare');
    if (compareEl && typeof variant.compare_at_price === 'number' && variant.compare_at_price > subscriptionPrice) {
      compareEl.textContent = formatMoney(variant.compare_at_price, compareEl);
      compareEl.classList.remove('tw-hidden');
      compareEl.hidden = false;
    } else if (compareEl) {
      compareEl.classList.add('tw-hidden');
      compareEl.hidden = true;
    }

    const subscribeSelected =
      subscribeOpt &&
      (subscribeOpt.classList.contains('tw-selected') ||
        subscribeOpt.getAttribute('aria-pressed') === 'true');

    const sellingPlanId = getSellingPlanId(variant);
    const sellingPlanInput = scope.querySelector('.tw-pdp-form input[name="selling_plan"]');
    if (subscribeSelected && sellingPlanInput) {
      sellingPlanInput.value = sellingPlanId;
    } else if (sellingPlanInput && !subscribeSelected) {
      sellingPlanInput.value = '';
    }
    if (subscribeOpt && sellingPlanId) {
      subscribeOpt.setAttribute('data-selling-plan', sellingPlanId);
    }
    if (wrapper && sellingPlanId) {
      wrapper.setAttribute('data-selling-plan-id', sellingPlanId);
    }

    const ctaBtn = scope.querySelector('.tw-cta-button');
    const sellable = isVariantSellable(variant);
    if (ctaBtn) {
      ctaBtn.disabled = !sellable;
    }

    if (subscribeSelected) {
      const ctaPrice = scope.querySelector('.tw-cta-price');
      if (ctaPrice) {
        ctaPrice.textContent = formatMoney(subscriptionPrice, ctaPrice);
      }
    }

    updateFormVariant(sectionId, variantId);

    syncPreorderUI(sectionId, variantId);

    if (window.TwPdpWaitlist && typeof window.TwPdpWaitlist.syncForVariantId === 'function') {
      window.TwPdpWaitlist.syncForVariantId(sectionId, variantId);
    }
  }

  function updatePurchaseTypeFromDoc(sectionId, doc) {
    const newPurchaseType = doc.querySelector('.tw-purchase-type-wrapper');
    const currentPurchaseType = document.querySelector(
      '#shopify-section-' + sectionId + ' .tw-purchase-type-wrapper'
    );
    if (!newPurchaseType || !currentPurchaseType) return;

    const newSubscribe = newPurchaseType.querySelector('[data-purchase-type="subscribe"]');
    const currentSubscribe = currentPurchaseType.querySelector(
      '[data-purchase-type="subscribe"]'
    );
    if (newSubscribe && currentSubscribe) {
      currentSubscribe.setAttribute('data-price', newSubscribe.getAttribute('data-price') || '');
      currentSubscribe.setAttribute(
        'data-selling-plan',
        newSubscribe.getAttribute('data-selling-plan') || ''
      );

      const newSubPrice = newSubscribe.querySelector('.tw-subscription-price');
      const curSubPrice = currentSubscribe.querySelector('.tw-subscription-price');
      if (newSubPrice && curSubPrice) {
        curSubPrice.innerHTML = newSubPrice.innerHTML;
      }

      const newCompare = newSubscribe.querySelector('.tw-subscription-compare');
      const curCompare = currentSubscribe.querySelector('.tw-subscription-compare');
      if (newCompare && curCompare) {
        curCompare.innerHTML = newCompare.innerHTML;
        curCompare.className = newCompare.className;
        if (newCompare.hasAttribute('hidden')) {
          curCompare.setAttribute('hidden', 'hidden');
        } else {
          curCompare.removeAttribute('hidden');
        }
      }
    }

    const newOnetime = newPurchaseType.querySelector('[data-purchase-type="onetime"]');
    const currentOnetime = currentPurchaseType.querySelector('[data-purchase-type="onetime"]');
    if (newOnetime && currentOnetime) {
      currentOnetime.setAttribute('data-price', newOnetime.getAttribute('data-price') || '');
      const onetimeVariantId = newOnetime.getAttribute('data-onetime-variant-id');
      if (onetimeVariantId) {
        currentOnetime.setAttribute('data-onetime-variant-id', onetimeVariantId);
      }
      currentOnetime.querySelectorAll('.tw-onetime-price').forEach(function (el, idx) {
        const src = newOnetime.querySelectorAll('.tw-onetime-price')[idx];
        if (src) el.innerHTML = src.innerHTML;
      });
    }

    currentPurchaseType.setAttribute(
      'data-subscription-price',
      newPurchaseType.getAttribute('data-subscription-price') || ''
    );
    currentPurchaseType.setAttribute(
      'data-onetime-price',
      newPurchaseType.getAttribute('data-onetime-price') || ''
    );
    currentPurchaseType.setAttribute(
      'data-selling-plan-id',
      newPurchaseType.getAttribute('data-selling-plan-id') || ''
    );

    const newQtySelector = newPurchaseType.querySelector('[id^="tw-qty-selector-"]');
    const currentQtySelector = currentPurchaseType.querySelector('[id^="tw-qty-selector-"]');
    if (newQtySelector && currentQtySelector) {
      currentQtySelector.innerHTML = newQtySelector.innerHTML;
    }

    const newSpecLine = newPurchaseType.querySelector('[data-tw-subscribe-spec-line]');
    const currentSpecLine = currentPurchaseType.querySelector('[data-tw-subscribe-spec-line]');
    if (newSpecLine && currentSpecLine) {
      currentSpecLine.textContent = newSpecLine.textContent;
    } else if (!newSpecLine && currentSpecLine) {
      currentSpecLine.remove();
    } else if (newSpecLine && !currentSpecLine) {
      const currentRow = currentPurchaseType
        .querySelector('.tw-auto-deliver-badge')
        ?.closest('.tw-col-span-2.tw-flex');
      if (currentRow) {
        currentRow.insertBefore(newSpecLine.cloneNode(true), currentRow.firstChild);
      }
    }
  }

  const ACCENT_ATTRS = [
    'data-tw-pdp-accent-color',
    'data-tw-pdp-accent-hover-color',
    'data-tw-pdp-accent-text-color'
  ];

  /**
   * Applies optional variant accent metafields to the stable product root.
   * Blank accent values remove the override and let the theme's default Tailwind classes win.
   */
  function syncVariantAccent(sectionId, sourceElement) {
    const root = document.getElementById('MainProduct-' + sectionId);
    if (!root) return;

    const source = sourceElement || root;
    const accentColor = (source.getAttribute('data-tw-pdp-accent-color') || '').trim();
    const hoverColor = (source.getAttribute('data-tw-pdp-accent-hover-color') || accentColor).trim();
    const textColor = (source.getAttribute('data-tw-pdp-accent-text-color') || '#ffffff').trim();

    if (!accentColor) {
      ACCENT_ATTRS.forEach(attr => root.removeAttribute(attr));
      root.style.removeProperty('--tw-pdp-accent-color');
      root.style.removeProperty('--tw-pdp-accent-hover-color');
      root.style.removeProperty('--tw-pdp-accent-text-color');
      return;
    }

    root.setAttribute('data-tw-pdp-accent-color', accentColor);
    root.setAttribute('data-tw-pdp-accent-hover-color', hoverColor);
    root.setAttribute('data-tw-pdp-accent-text-color', textColor);
    root.style.setProperty('--tw-pdp-accent-color', accentColor);
    root.style.setProperty('--tw-pdp-accent-hover-color', hoverColor);
    root.style.setProperty('--tw-pdp-accent-text-color', textColor);
  }

  function syncScentPalette(sectionId, sourceElement) {
    syncVariantAccent(sectionId, sourceElement);
  }

  /**
   * Fetches new section HTML and performs PARTIAL updates.
   * Only updates specific elements to preserve third-party app elements (Qikify, etc.)
   * @param {string} sectionId - The section ID
   * @param {string} productUrl - The product URL
   * @param {string} variantId - The new variant ID
   * @returns {Promise} - Resolves when updates are complete
   */
  function refreshSection(sectionId, productUrl, variantId) {
    if (!sectionId || !variantId) {
      return Promise.resolve('');
    }
    const requestId = (latestRefreshRequestBySection[sectionId] || 0) + 1;
    latestRefreshRequestBySection[sectionId] = requestId;

    const urlParams = new URLSearchParams(window.location.search);
    const viewParam = urlParams.get('view');
    const fetchUrl = buildSectionFetchUrl(productUrl, sectionId, variantId, viewParam);

    return fetch(fetchUrl)
      .then(response => {
        if (!response.ok) {
          throw new Error('Section refresh failed: ' + response.status);
        }
        return response.text();
      })
      .then(html => {
        // Ignore stale responses when users click variants rapidly.
        if (latestRefreshRequestBySection[sectionId] !== requestId) {
          return '';
        }
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const newProductRoot = doc.querySelector('#MainProduct-' + sectionId);
        const currentProductRoot = document.getElementById('MainProduct-' + sectionId);
        if (newProductRoot) {
          syncVariantAccent(sectionId, newProductRoot);
          if (currentProductRoot) {
            const newAvailability = newProductRoot.getAttribute('data-tw-pdp-availability');
            if (newAvailability) {
              currentProductRoot.setAttribute('data-tw-pdp-availability', newAvailability);
            }
          }
        }
        
        // Update price
        const newPrice = doc.querySelector('.price');
        const currentPrice = document.querySelector(`#shopify-section-${sectionId} .price`);
        if (newPrice && currentPrice) {
          currentPrice.innerHTML = newPrice.innerHTML;
          currentPrice.className = newPrice.className;
        }
        
        // Update product media/gallery
        const newMedia = doc.querySelector('.product__media-list, .product__media-wrapper');
        const currentMedia = document.querySelector(`#shopify-section-${sectionId} .product__media-list, #shopify-section-${sectionId} .product__media-wrapper`);
        if (newMedia && currentMedia) {
          currentMedia.innerHTML = newMedia.innerHTML;
        }

        if (window.TwIngredientsVariantSync && typeof window.TwIngredientsVariantSync.sync === 'function') {
          window.TwIngredientsVariantSync.sync(variantId, null, null, sectionId);
        }
        
        // Update buy button state
        const newBuyButton = doc.querySelector('.product-form__submit');
        const currentBuyButton = document.querySelector(`#shopify-section-${sectionId} .product-form__submit`);
        if (newBuyButton && currentBuyButton) {
          currentBuyButton.disabled = newBuyButton.disabled;
          currentBuyButton.innerHTML = newBuyButton.innerHTML;
        }
        
        // Update our Tailwind scent selector
        const newScentSelector = doc.querySelector('[id^="tw-scent-selector-"]');
        const currentScentSelector = document.querySelector(`#shopify-section-${sectionId} [id^="tw-scent-selector-"]`);
        if (newScentSelector && currentScentSelector) {
          currentScentSelector.innerHTML = newScentSelector.innerHTML;
        }
        
        // Update our Tailwind usage text
        const newUsageText = doc.querySelector('.tw-usage-text-wrapper');
        const currentUsageText = document.querySelector(`#shopify-section-${sectionId} .tw-usage-text-wrapper`);
        if (newUsageText && currentUsageText) {
          currentUsageText.outerHTML = newUsageText.outerHTML;
        } else if (newUsageText && !currentUsageText) {
          // Usage text appeared for new variant - find insertion point
          const qtySelector = document.querySelector(`#shopify-section-${sectionId} [id^="tw-qty-selector-"]`);
          if (qtySelector) {
            qtySelector.insertAdjacentHTML('beforebegin', newUsageText.outerHTML);
          }
        } else if (!newUsageText && currentUsageText) {
          // Usage text no longer exists for new variant - remove it
          currentUsageText.remove();
        }

        // Update preorder info block
        const newPreorderBlock = doc.querySelector('.tw-pdp-block--preorder-info');
        const currentPreorderBlock = document.querySelector(
          `#shopify-section-${sectionId} .tw-pdp-block--preorder-info`
        );
        if (newPreorderBlock && currentPreorderBlock) {
          currentPreorderBlock.innerHTML = newPreorderBlock.innerHTML;
        }
        
        // Update our Tailwind CTA button (price and state)
        const newCtaButton = doc.querySelector('.tw-cta-button');
        const currentCtaButton = document.querySelector(`#shopify-section-${sectionId} .tw-cta-button`);
        if (newCtaButton && currentCtaButton) {
          // Update button disabled state
          currentCtaButton.disabled = newCtaButton.disabled;
          
          // Update button text (Add to Cart / Sold Out)
          const newText = newCtaButton.querySelector('.tw-cta-text');
          const currentText = currentCtaButton.querySelector('.tw-cta-text');
          if (newText && currentText) {
            currentText.innerHTML = newText.innerHTML;
          }
          
          // Update price badge
          const newPrice = newCtaButton.querySelector('.tw-cta-price');
          const currentPrice = currentCtaButton.querySelector('.tw-cta-price');
          if (newPrice && currentPrice) {
            currentPrice.innerHTML = newPrice.innerHTML;
          }
        }
        
        // Update our Tailwind CTA form inputs (variant ID and selling plan)
        const newCtaForm = doc.querySelector('.tw-pdp-form');
        const currentCtaForm = document.querySelector(`#shopify-section-${sectionId} .tw-pdp-form`);
        
        if (newCtaForm && currentCtaForm) {
          // Update variant ID
          const newVariantInput = newCtaForm.querySelector('input[name="id"]');
          const currentVariantInput = currentCtaForm.querySelector('input[name="id"]');
          if (newVariantInput && currentVariantInput) {
            currentVariantInput.value = newVariantInput.value;
          }
          
          // Update selling plan ID (for subscriptions)
          const newSellingPlanInput = newCtaForm.querySelector('input[name="selling_plan"]');
          const currentSellingPlanInput = currentCtaForm.querySelector('input[name="selling_plan"]');
          if (newSellingPlanInput && currentSellingPlanInput) {
            currentSellingPlanInput.value = newSellingPlanInput.value;
          }
          
        }
        
        // Partial purchase-type update (preserve click handlers; refresh bundle dropdown)
        updatePurchaseTypeFromDoc(sectionId, doc);

        const newQtySelector = doc.querySelector('[id^="tw-qty-selector-"]');
        const currentQtySelector = document.querySelector(
          `#shopify-section-${sectionId} [id^="tw-qty-selector-"]`
        );
        if (newQtySelector && currentQtySelector && !currentQtySelector.closest('.tw-purchase-type-wrapper')) {
          currentQtySelector.innerHTML = newQtySelector.innerHTML;
        }

        // Ensure form variant is correct after DOM updates
        updateFormVariant(sectionId, variantId);
        syncSubscribePricing(sectionId, variantId);
        
        // Re-sync purchase type default after DOM updates
        // (international restrictions + variant metafield default_onetime_purchase)
        if (typeof window.syncPurchaseTypeDefault === 'function') {
          setTimeout(window.syncPurchaseTypeDefault, 10);
        }

        syncVariantAccent(sectionId);

        if (window.TwIngredientsVariantSync && typeof window.TwIngredientsVariantSync.sync === 'function') {
          window.TwIngredientsVariantSync.sync(variantId, null, null, sectionId);
        }

        if (window.TwFaqScentSync && typeof window.TwFaqScentSync.sync === 'function') {
          window.TwFaqScentSync.sync(null, variantId);
        }

        const newTwPdpPrice = doc.querySelector('.tw-pdp-price');
        const currentTwPdpPrice = document.querySelector(`#shopify-section-${sectionId} .tw-pdp-price`);
        if (newTwPdpPrice && currentTwPdpPrice) {
          currentTwPdpPrice.outerHTML = newTwPdpPrice.outerHTML;
        }

        const newWaitlist = doc.querySelector('[data-tw-pdp-waitlist]');
        const currentWaitlist = document.querySelector(
          `#shopify-section-${sectionId} [data-tw-pdp-waitlist]`
        );
        if (newWaitlist && currentWaitlist) {
          const hadContent = currentWaitlist.innerHTML.trim().length > 0;
          currentWaitlist.innerHTML = newWaitlist.innerHTML;
          if (newWaitlist.innerHTML.trim() && !hadContent) {
            try {
              window.dispatchEvent(new Event('resize'));
            } catch (e) {
              /* ignore */
            }
          }
        }

        const newPreorder = doc.querySelector('[data-tw-pdp-preorder]');
        const currentPreorder = document.querySelector(
          `#shopify-section-${sectionId} [data-tw-pdp-preorder]`
        );
        if (newPreorder && currentPreorder) {
          currentPreorder.outerHTML = newPreorder.outerHTML;
        } else if (newPreorder && !currentPreorder) {
          const purchaseType = document.querySelector(
            `#shopify-section-${sectionId} .tw-pdp-block--purchase-type`
          );
          if (purchaseType) {
            purchaseType.insertAdjacentHTML('afterend', newPreorder.outerHTML);
          } else {
            const benefits = document.querySelector(
              `#shopify-section-${sectionId} .tw-pdp-block--benefits`
            );
            if (benefits) {
              benefits.insertAdjacentHTML('afterend', newPreorder.outerHTML);
            }
          }
        } else if (!newPreorder && currentPreorder) {
          currentPreorder.remove();
        }

        syncPreorderUI(sectionId, variantId);
        notifyVariantChange(sectionId, variantId);

        if (window.TwPdpWaitlist && typeof window.TwPdpWaitlist.syncForVariantId === 'function') {
          window.TwPdpWaitlist.syncForVariantId(sectionId, variantId);
        }

        return html;
      })
      .catch(err => {
        if (latestRefreshRequestBySection[sectionId] !== requestId) {
          return '';
        }
        console.warn('[TwVariantSelector] Section refresh failed:', err);
        return '';
      });
  }

  /**
   * Updates visual state for selector cards
   * @param {HTMLElement} container - The selector container
   * @param {HTMLElement} selectedCard - The newly selected card
   * @param {string} cardClass - The card class name (e.g., 'tw-scent-card', 'tw-qty-card')
   * @param {string} checkClass - The check class name (e.g., 'tw-scent-check', 'tw-qty-check')
   */
  function updateCardStates(container, selectedCard, cardClass, checkClass) {
    // Deselect all cards
    container.querySelectorAll(`.${cardClass}`).forEach(card => {
      card.classList.remove('tw-border-brand-primary', 'tw-bg-[#c8effe]');
      card.classList.add('tw-border-gray-200', 'tw-bg-white');
      const check = card.querySelector(`.${checkClass}`);
      if (check) {
        check.classList.add('tw-hidden');
        check.classList.remove('tw-flex');
      }
    });
    
    // Select the clicked card
    selectedCard.classList.remove('tw-border-gray-200', 'tw-bg-white');
    selectedCard.classList.add('tw-border-brand-primary', 'tw-bg-[#c8effe]');
    const check = selectedCard.querySelector(`.${checkClass}`);
    if (check) {
      check.classList.remove('tw-hidden');
      check.classList.add('tw-flex');
    }
  }

  /**
   * Full variant selection handler
   * @param {Object} options - Configuration options
   * @param {string} options.sectionId - The section ID
   * @param {string} options.productUrl - The product URL  
   * @param {string} options.variantId - The new variant ID
   * @param {HTMLElement} options.container - The selector container
   * @param {HTMLElement} options.selectedCard - The newly selected card
   * @param {string} options.cardClass - The card class name
   * @param {string} [options.checkClass] - Check class (cards only)
   * @param {boolean} [options.skipCardUpdate] - If true, skip updateCardStates (e.g. scent dropdown)
   */
  function selectVariant(options) {
    const { sectionId, productUrl, variantId, container, selectedCard, cardClass, checkClass, skipCardUpdate } = options;
    const resolvedUrl = resolveProductUrl(
      productUrl || (container && container.dataset.productUrl) || '',
      sectionId
    );

    if (!variantId || !sectionId) return Promise.resolve('');

    if (!skipCardUpdate && container && selectedCard && cardClass && checkClass) {
      updateCardStates(container, selectedCard, cardClass, checkClass);
    }

    updateFormVariant(sectionId, variantId);
    updateUrl(variantId);

    syncSubscribePricing(sectionId, variantId);
    notifyVariantChange(sectionId, variantId);

    return refreshSection(sectionId, resolvedUrl, variantId).then(function (html) {
      syncSubscribePricing(sectionId, variantId);
      notifyVariantChange(sectionId, variantId);
      return html;
    });
  }

  function initTwScentPalettes() {
    document.querySelectorAll('[id^="MainProduct-"]').forEach(function (el) {
      const m = el.id.match(/^MainProduct-(.+)$/);
      if (!m) return;
      syncVariantAccent(m[1]);
      const formVariant = document.querySelector(
        `#product-form-${m[1]} input[name="id"], .tw-pdp-form input[name="id"]`
      );
      syncPreorderUI(m[1], formVariant ? formVariant.value : null);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTwScentPalettes);
  } else {
    initTwScentPalettes();
  }

  // Public API
  return {
    updateFormVariant,
    updateUrl,
    refreshSection,
    updateCardStates,
    selectVariant,
    syncScentPalette,
    syncVariantAccent,
    syncSubscribePricing,
    syncPreorderUI,
    getVariantFromProductData,
    isVariantSellable,
    isVariantPreorder,
    getSellingPlanId
  };
})();
